import { useCallback, useEffect, useRef, useState } from 'react'
import { useViewport } from './use-viewport'

/**
 * Sidebar collapse state.
 *
 * Behaviour:
 *   - Desktop (≥1100px) : user controls collapse via toggle. Default = persisted pref (expanded if none).
 *   - Tablet  (720-1099): auto-collapses to icon-rail on entry, but the toggle still expands/collapses
 *                          (e.g. iPad users can tap the chevron to open the sidebar).
 *   - Mobile  (<720px) : collapse is irrelevant — the sidebar becomes a drawer.
 *
 * Persists the desktop preference to localStorage so a user's choice survives
 * reloads. Tablet toggles are in-memory only — they don't overwrite the saved
 * desktop pref, since landscape iPads frequently straddle the breakpoint.
 */

export const SIDEBAR_COLLAPSE_STORAGE_KEY = 'lms.sidebar.collapsed'
export const SIDEBAR_WIDTH_STORAGE_KEY = 'lms.sidebar.width'

export const SIDEBAR_WIDTH_MIN = 160
export const SIDEBAR_WIDTH_MAX = 360
export const SIDEBAR_WIDTH_DEFAULT = 190

function clampWidth(value: number): number {
  if (!Number.isFinite(value)) return SIDEBAR_WIDTH_DEFAULT
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(value)))
}

export interface SidebarCollapseState {
  collapsed: boolean
  toggle: () => void
  setCollapsed: (next: boolean) => void
  width: number
  setWidth: (next: number) => void
}

type ViewportClass = 'mobile' | 'tablet' | 'desktop'

function readPersisted(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const raw = window.localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY)
    return raw === 'true'
  } catch {
    return false
  }
}

function persist(value: boolean): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, value ? 'true' : 'false')
  } catch {
    // localStorage may throw in private mode; ignore — state still works in-memory.
  }
}

function readPersistedWidth(): number {
  if (typeof window === 'undefined') return SIDEBAR_WIDTH_DEFAULT
  try {
    const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY)
    if (raw == null) return SIDEBAR_WIDTH_DEFAULT
    return clampWidth(Number.parseInt(raw, 10))
  } catch {
    return SIDEBAR_WIDTH_DEFAULT
  }
}

function persistWidth(value: number): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(value))
  } catch {
    // ignore
  }
}

function classifyViewport(isMobile: boolean, isTablet: boolean): ViewportClass {
  if (isMobile) return 'mobile'
  if (isTablet) return 'tablet'
  return 'desktop'
}

export function useSidebarCollapse(): SidebarCollapseState {
  const { isTablet, isMobile } = useViewport()
  const currentViewport = classifyViewport(isMobile, isTablet)

  // Single source of truth for what's rendered. Seeded from the persisted
  // desktop pref; mutated by user toggles AND by viewport transitions.
  const [collapsed, setCollapsedState] = useState<boolean>(() => readPersisted())

  // Track the last viewport so we can react to transitions, not just current value.
  const lastViewportRef = useRef<ViewportClass | null>(null)

  useEffect(() => {
    const prev = lastViewportRef.current
    if (prev === currentViewport) return

    if (currentViewport === 'tablet' && prev !== 'tablet') {
      // Entering tablet (incl. SSR-default → real width on mount): collapse
      // by default. In-memory only — don't touch persisted desktop pref.
      setCollapsedState(true)
    } else if (currentViewport === 'desktop' && prev === 'tablet') {
      // Coming back to desktop from tablet: restore persisted pref.
      setCollapsedState(readPersisted())
    }
    lastViewportRef.current = currentViewport
  }, [currentViewport])

  const setCollapsed = useCallback(
    (next: boolean): void => {
      setCollapsedState(next)
      // Persist only on desktop. Tablet decisions stay in-memory.
      if (currentViewport === 'desktop') persist(next)
    },
    [currentViewport],
  )

  const toggle = useCallback((): void => {
    setCollapsedState((prev) => {
      const next = !prev
      if (currentViewport === 'desktop') persist(next)
      return next
    })
  }, [currentViewport])

  const [width, setWidthState] = useState<number>(() => readPersistedWidth())

  const setWidth = useCallback(
    (next: number): void => {
      const clamped = clampWidth(next)
      setWidthState(clamped)
      if (currentViewport === 'desktop') persistWidth(clamped)
    },
    [currentViewport],
  )

  return { collapsed, toggle, setCollapsed, width, setWidth }
}
